// pages/category/CityCategory.jsx
import React, { useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "../../theme/ThemeProvider";
import { useSEO } from "../../hooks/useSEO";
import Layout from "../../components/Layout";
import ErrorState from "../../components/state/ErrorState";
import EmptyState from "../../components/state/EmptyState";
import {
  fetchCategoryBrands,
  clearCategoryData,
} from "../../store/slices/categorySlice";
import { findCityBySlug } from "../../store/slices/citySlice";
import { HeroSection } from "./HeroSection";
import { TopSellingProducts } from "./TopSellingProducts";
import { HowItWorks } from "./HowItWorks";
import { LandingSkeleton } from "./LandingSkeleton";

const formatName = (slug) => {
  if (!slug) return "";
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const CityCategory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { citySlug, categorySlug } = useParams();
  const { isDarkMode } = useTheme();

  const { selectedCity, findingCity, findCityError } = useSelector(
    (state) => state.city,
  );

  const { loading: homeLoading } = useSelector((state) => state.home);
  const {
    data: categoryData,
    loading: categoryLoading,
    error: categoryError,
  } = useSelector((state) => state.category);

  const cleanCategorySlug = categorySlug;
  const cleanCitySlug = citySlug;

  const deviceName = useMemo(
    () => formatName(cleanCategorySlug),
    [cleanCategorySlug],
  );
  const cleanDeviceName = useMemo(
    () => formatName(cleanCategorySlug),
    [cleanCategorySlug],
  );
  const cityName = useMemo(() => formatName(cleanCitySlug), [cleanCitySlug]);

  const isLoading = homeLoading || categoryLoading || findingCity;
  const hasError = categoryError || findCityError;

  // Extract data from categoryData
  const category = categoryData?.data?.category || null;
  const brands = categoryData?.data?.brands || [];
  const topSelling = categoryData?.data?.top_selling || [];

  // ============= Handle City Selection =============
  useEffect(() => {
    if (cleanCitySlug) {
      const currentCitySlug = selectedCity?.slug;
      // If URL city doesn't match selected city, find and auto-select it
      if (currentCitySlug !== cleanCitySlug) {
        dispatch(findCityBySlug(cleanCitySlug));
      }
    }
  }, [cleanCitySlug, selectedCity?.slug, dispatch]);

  // Fetch category data when city matches URL
  useEffect(() => {
    if (
      selectedCity?.slug === cleanCitySlug &&
      cleanCategorySlug &&
      !findingCity &&
      !findCityError
    ) {
      dispatch(clearCategoryData());
      dispatch(fetchCategoryBrands(cleanCategorySlug));
    }
  }, [
    dispatch,
    cleanCategorySlug,
    cleanCitySlug,
    selectedCity?.slug,
    findingCity,
    findCityError,
  ]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearCategoryData());
    };
  }, [dispatch]);

  // ============= SEO =============
  const seoParams = useMemo(() => {
    if (isLoading || !category || !selectedCity) return null;

    return {
      categorySlug: categorySlug,
      categoryName: category?.name || "",
      categoryDescription: category?.description || "",
      brandCount: brands?.length || 0,
      cityName: selectedCity?.name || "",
      stateName: selectedCity?.state?.name || "",
      citySlug: selectedCity?.slug || "",
    };
  }, [isLoading, category, categorySlug, brands, selectedCity]);

  useSEO(
    selectedCity?.slug
      ? `/sell-used-online/in/${selectedCity.slug}/${categorySlug}`
      : null,
    seoParams,
  );

  const handleRetry = useCallback(() => {
    if (cleanCategorySlug) {
      dispatch(clearCategoryData());
      dispatch(fetchCategoryBrands(cleanCategorySlug));
    }
    if (cleanCitySlug && selectedCity?.slug !== cleanCitySlug) {
      dispatch(findCityBySlug(cleanCitySlug));
    }
  }, [dispatch, cleanCategorySlug, cleanCitySlug, selectedCity?.slug]);

  const heroProps = useMemo(
    () => ({
      title: `Sell Used ${deviceName} for Instant Cash in ${selectedCity?.name || cityName}`,
      subtitle: `Get the best price for your ${cleanDeviceName.toLowerCase()} in ${selectedCity?.name || cityName}${
        selectedCity?.state?.name ? `, ${selectedCity.state.name}` : ""
      } - Fast, safe, and convenient`,
    }),
    [deviceName, cleanDeviceName, selectedCity, cityName],
  );

  // Show loading while city is being found or doesn't match
  if (
    isLoading ||
    findingCity ||
    !selectedCity ||
    selectedCity?.slug !== cleanCitySlug
  ) {
    return (
      <Layout>
        <LandingSkeleton isDarkMode={isDarkMode} />
      </Layout>
    );
  }

  if (hasError) {
    return (
      <Layout>
        <ErrorState
          error={categoryError || findCityError}
          onRetry={handleRetry}
          isDarkMode={isDarkMode}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <HeroSection
        cleanDeviceName={cleanDeviceName}
        categorySlug={cleanCategorySlug}
        getHeroTitle={() => heroProps.title}
        getHeroSubtitle={() => heroProps.subtitle}
        quickBrands={brands}
        isDarkMode={isDarkMode}
        navigate={navigate}
        categoryName={category?.name}
        city={selectedCity}
      />

      {topSelling.length > 0 && (
        <TopSellingProducts
          cityName={selectedCity?.name}
          products={topSelling}
          category={category?.name}
          isDarkMode={isDarkMode}
          navigate={navigate}
          citySlug={selectedCity?.slug}
        />
      )}

      <HowItWorks isDarkMode={isDarkMode} />

      {!topSelling.length && !brands.length && !isLoading && (
        <div className="container px-4 py-12 mx-auto max-w-7xl">
          <EmptyState isDarkMode={isDarkMode} />
        </div>
      )}
    </Layout>
  );
};

export default CityCategory;
